import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, RefreshCw, Webhook, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { CodeBlock } from './CodeBlock';

const API = '/api';

const statusColor = (s) => ({
  sent: 'text-emerald-400', failed: 'text-red-400', error: 'text-red-400', queued: 'text-cyan-400',
  pending: 'text-amber-400', skipped_no_rcs: 'text-zinc-400', skipped: 'text-zinc-400',
}[s] || 'text-zinc-400');

const WebhookBadge = ({ m }) => {
  if (!m.webhook_url && !m.webhook_status) return <span className="text-zinc-600">—</span>;
  if (m.webhook_status === 'delivered') return <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-3 h-3" /> entregado</span>;
  if (m.webhook_status === 'failed') return <span className="flex items-center gap-1 text-red-400" title={m.webhook_last_error || ''}><XCircle className="w-3 h-3" /> fallo</span>;
  return <span className="flex items-center gap-1 text-zinc-500"><Clock className="w-3 h-3" /> pendiente</span>;
};

export const ApiTesterPanel = ({ apiKey, setApiKey }) => {
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState({ phone: '', message: 'Hola {amigo|crack}, esto es una prueba desde la API', profile_id: '', type: 'both', human_typing: true, split_messages: false, external_id: '' });
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [recent, setRecent] = useState([]);
  const pollRef = useRef(null);

  const loadRecent = async () => {
    try { setRecent((await axios.get(`${API}/api-messages?limit=20`)).data); } catch (e) {}
  };

  useEffect(() => {
    axios.get(`${API}/profiles`).then((r) => {
      setProfiles(r.data);
      if (r.data[0] && !form.profile_id) setForm((f) => ({ ...f, profile_id: r.data[0].id }));
    }).catch(() => {});
    loadRecent();
    const t = setInterval(loadRecent, 10000);
    return () => { clearInterval(t); if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const pollStatus = (messageId, key) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/v1/status/${messageId}`, { headers: { 'X-API-Key': key } });
        setTracking(r.data);
        if (['sent', 'failed', 'error', 'skipped_no_rcs', 'skipped'].includes(r.data.status)) { clearInterval(pollRef.current); loadRecent(); }
      } catch (e) {}
    }, 5000);
  };

  const send = async () => {
    if (!apiKey) return toast.error('Pega una API key');
    if (!form.phone || !form.profile_id) return toast.error('Telefono y perfil son requeridos');
    setSending(true);
    setResponse(null);
    setTracking(null);
    try {
      const r = await axios.post(`${API}/v1/send`, { ...form, external_id: form.external_id || undefined }, { headers: { 'X-API-Key': apiKey } });
      setResponse({ status: r.status, data: r.data });
      setTracking({ status: 'pending', message_id: r.data.message_id });
      pollStatus(r.data.message_id, apiKey);
      toast.success('Mensaje encolado. Lo enviara el perfil seleccionado.');
      loadRecent();
    } catch (e) {
      setResponse({ status: e.response?.status || 0, data: e.response?.data || { error: e.message } });
      toast.error(e.response?.data?.detail || 'Error en la llamada');
    } finally { setSending(false); }
  };

  const inputCls = 'w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/50';

  return (
    <div className="space-y-5" data-testid="api-tester-panel">
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-5 space-y-3">
          <h2 className="text-base font-semibold text-white">Probar POST /api/v1/send</h2>
          <label className="text-xs text-zinc-400 space-y-1 block"><span>API Key</span>
            <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="rcs_…" data-testid="tester-api-key-input" className={`${inputCls} font-mono`} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-zinc-400 space-y-1 block"><span>Telefono</span>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+5491122334455" data-testid="tester-phone-input" className={`${inputCls} font-mono`} />
            </label>
            <label className="text-xs text-zinc-400 space-y-1 block"><span>Perfil (profile_id)</span>
              <select value={form.profile_id} onChange={(e) => setForm({ ...form, profile_id: e.target.value })} data-testid="tester-profile-select" className={inputCls}>
                {profiles.map((p) => <option key={p.id} value={p.id}>{p.name} {p.status === 'connected' ? '● conectado' : '○ desconectado'}</option>)}
              </select>
            </label>
          </div>
          <label className="text-xs text-zinc-400 space-y-1 block"><span>Mensaje</span>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} data-testid="tester-message-input" className={inputCls} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-zinc-400 space-y-1 block"><span>Tipo</span>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="tester-type-select" className={inputCls}>
                <option value="rcs">rcs (solo RCS)</option><option value="both">both (RCS o SMS)</option><option value="sms">sms</option>
              </select>
            </label>
            <label className="text-xs text-zinc-400 space-y-1 block"><span>external_id (opcional)</span>
              <input value={form.external_id} onChange={(e) => setForm({ ...form, external_id: e.target.value })} placeholder="pedido-8812" data-testid="tester-external-id-input" className={`${inputCls} font-mono`} />
            </label>
          </div>
          <div className="flex items-center gap-4 text-xs text-zinc-300">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.human_typing} onChange={(e) => setForm({ ...form, human_typing: e.target.checked })} data-testid="tester-human-typing" className="accent-cyan-500" /> Tipeo humano</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.split_messages} onChange={(e) => setForm({ ...form, split_messages: e.target.checked })} data-testid="tester-split" className="accent-cyan-500" /> Separar por ---</label>
          </div>
          <button onClick={send} disabled={sending} data-testid="tester-send-btn" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Send className="w-4 h-4" /> {sending ? 'Enviando…' : 'Enviar prueba'}
          </button>
        </div>

        <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-5 space-y-3">
          <h2 className="text-base font-semibold text-white">Respuesta</h2>
          {response ? (
            <>
              <p className={`text-xs font-mono ${response.status >= 200 && response.status < 300 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="tester-response-status">HTTP {response.status}</p>
              <CodeBlock code={JSON.stringify(response.data, null, 2)} language="json" testId="tester-response" />
            </>
          ) : <p className="text-xs text-zinc-500">Envia una prueba para ver la respuesta JSON aqui.</p>}
          {tracking && (
            <div className="bg-zinc-950/60 rounded-lg p-3 text-xs space-y-1" data-testid="tester-tracking">
              <div className="flex items-center gap-2 text-zinc-400"><RefreshCw className="w-3 h-3 animate-spin" style={{ animationPlayState: ['sent', 'failed', 'error', 'skipped_no_rcs', 'skipped'].includes(tracking.status) ? 'paused' : 'running' }} /> Seguimiento GET /api/v1/status/{tracking.message_id}</div>
              <p>Estado: <b className={statusColor(tracking.status)} data-testid="tester-tracking-status">{tracking.status}</b>{tracking.rcs !== undefined && tracking.rcs !== null && <span className="text-zinc-500"> · {tracking.rcs ? 'RCS' : 'SMS'}</span>}{tracking.error && <span className="text-red-400"> · {tracking.error}</span>}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-5 space-y-3" data-testid="api-recent-messages">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Ultimos mensajes via API</h2>
          <button onClick={loadRecent} data-testid="refresh-recent-btn" className="text-zinc-500 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {recent.length === 0 ? <p className="text-xs text-zinc-500">Todavia no hay mensajes enviados por API.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Telefono</th><th className="py-2 pr-3">Mensaje</th><th className="py-2 pr-3">Perfil</th><th className="py-2 pr-3">Key</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3"><span className="flex items-center gap-1"><Webhook className="w-3 h-3" /> Webhook</span></th>
              </tr></thead>
              <tbody>
                {recent.map((m) => (
                  <tr key={m.id} className="border-b border-white/[0.03] text-zinc-300" data-testid={`api-message-row-${m.id}`}>
                    <td className="py-2 pr-3 font-mono text-zinc-500 whitespace-nowrap">{m.created_at ? new Date(m.created_at).toLocaleString('es-ES') : ''}</td>
                    <td className="py-2 pr-3 font-mono">{m.phone}</td>
                    <td className="py-2 pr-3 text-zinc-400 max-w-[260px] truncate">{m.message_text}</td>
                    <td className="py-2 pr-3">{m.profile_name || '—'}</td>
                    <td className="py-2 pr-3 text-zinc-500">{m.api_key_name || '—'}</td>
                    <td className={`py-2 pr-3 font-mono ${statusColor(m.status)}`}>{m.status}{m.rcs === true ? ' · RCS' : m.rcs === false ? ' · SMS' : ''}</td>
                    <td className="py-2 pr-3"><WebhookBadge m={m} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
